import { AdminService, UserInfoResolver } from '@miq/platform';
import { Component, inject, OnInit, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  Validators,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogActions } from '@angular/material/dialog';
import { IOption, ILocationPermission, IUser, IUserPermission } from '@miq/shared-models';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import { GlobalSelectionComponent } from '../../components/global-selection/global-selection.component';
import { TitleCasePipe } from '@angular/common';
import { SubSelectionComponent } from '../../components/sub-selection/sub-selection.component';
import { L10nTranslationModule } from 'angular-l10n';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatError, MatHint } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatInput } from '@angular/material/input';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'miq-add-location',
  templateUrl: './add-location.component.html',
  styleUrl: './add-location.component.scss',
  imports: [
    GlobalSelectionComponent,
    SubSelectionComponent,
    FormsModule,
    ReactiveFormsModule,
    L10nTranslationModule,
    MatIcon,
    MatFormField,
    MatSelect,
    MatOption,
    MatInput,
    MatError,
    MatHint,
    MatDialogActions,
    TitleCasePipe
],
})
export class AddLocationComponent implements OnInit, OnDestroy {
  public selectedMarketsCount: number;
  public selectedDistrictCount: number;
  public selectedNeighborCount: number;
  public userPermissions: IUserPermission;
  public invalidUserPermissions = true;
  public districtsList: IOption[] = [];
  public neighborhoodList: IOption[] = [];
  public subMarketList: IOption[] = [];
  public permissionStatus: IOption[] = [];
  public globalRegion: IOption;
  public confidentialityLevels: IOption[] = [];
  public permissionRecord: ILocationPermission; // record which is to be editted
  public recordsArray: ILocationPermission[] = [];
  public subMarket: number;
  public district: number;
  public neighborhood: number;
  public pageType: string;
  public userInfo: IUser;
  public confidentiality: IOption;
  public formGroup: FormGroup;
  public showItems = false;
  private id: number;
  public dialogRef = inject(MatDialogRef<AddLocationComponent>);
  private readonly dialog = inject(MatDialog);
  public injectedData = inject<any>(MAT_DIALOG_DATA);
  private readonly httpService = inject(AdminService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly resolver = inject(UserInfoResolver);
  private readonly cdr = inject(ChangeDetectorRef);
  constructor() {
    this.formGroup = this.formBuilder.group({
      businessJustification: ['', [Validators.required, Validators.maxLength(250), this.whitespaceValidator]],
      confidentialityFC: ['', [Validators.required]],
    });
    this.userPermissions = {
      globalRegion: { key: -1, value: '' },
      country: { key: -1, value: '' },
      division: { key: -1, value: '' },
      market: { key: -1, value: '' },
      subMarket: [{ key: -1, value: '' }],
      district: [{ key: -1, value: '' }],
      productType: { key: -1, value: '' },
      neighborhood: [{ key: -1, value: '' }],
    };
  }
  ngOnInit() {
    forkJoin(this.httpService.getPermissionStatuses(), this.httpService.getUserConfidentialityLevels())
      .pipe(take(1))
      .subscribe(res => {
        this.initUserSettings(res);
        this.cdr.markForCheck();
      });
  }
  private initUserSettings([permissionStatuses, confLevels]) {
    this.permissionStatus = permissionStatuses;
    this.confidentialityLevels = confLevels;
    this.userInfo = this.resolver.resolve();
    this.permissionRecord = this.injectedData.locationPermission;
    if (this.permissionRecord) {
      const { confidentialityTypeId: key } = this.permissionRecord;
      this.confidentiality =
        this.confidentialityLevels.find(option => option.key === key) || this.confidentialityLevels[0];
      this.setConfidentialityLevel(this.confidentiality);
      this.pageType = this.injectedData.pageType;
      if (this.permissionRecord && this.permissionRecord.businessJustification) {
        this.businessJustification.setValue(this.permissionRecord.businessJustification);
      }
    }
    this.id = this.injectedData.locationPermission ? this.injectedData.locationPermission.id : -1;
  }
  // Advanced search event handler
  public openSubselection(event) {
    if (event === 'close') {
      this.showItems = false;
      return;
    }
    if (event) {
      this.showItems = !this.showItems;
      if (this.permissionRecord && this.permissionRecord.subMarketId) {
        this.subMarket = this.permissionRecord.subMarketId;
        this.userPermissions.subMarket = [
          { key: this.permissionRecord.subMarketId, value: this.permissionRecord.subMarketVal },
        ];
        this.getDistricts(this.subMarket, true);
        this.selectedMarketsCount = 1;
      }
      if (this.permissionRecord && this.permissionRecord.districtId) {
        this.district = this.permissionRecord.districtId;
        this.userPermissions.district = [
          { key: this.permissionRecord.districtId, value: this.permissionRecord.districtVal },
        ];
        this.getDistricts(this.subMarket, true);
        this.selectedDistrictCount = 1;
      } else {
        this.getDistricts(event.key, true);
      }
      if (this.permissionRecord && this.permissionRecord.neighborhoodId) {
        this.neighborhood = this.permissionRecord.neighborhoodId;
        this.userPermissions.neighborhood = [
          { key: this.permissionRecord.neighborhoodId, value: this.permissionRecord.neighborhoodVal },
        ];
        this.getNeighbourhoods(this.district);
        this.selectedDistrictCount = 1;
      }
    }
  }
  public getDistricts(selectedSubmarket, key?: boolean) {
    if (selectedSubmarket) {
      const selectedValue = key ? selectedSubmarket : selectedSubmarket[0].key;
      this.httpService
        .getDistricts(selectedValue)
        .pipe(take(1))
        .subscribe(result => {
          this.districtsList = result;
          this.cdr.markForCheck();
        });
      if (this.permissionRecord && this.permissionRecord.districtId) {
        this.district = this.permissionRecord.districtId;
      }
    }
  }
  public getNeighbourhoods(district) {
    if (district) {
      this.httpService
        .getNeighbourhoods(district)
        .pipe(take(1))
        .subscribe(result => {
          this.neighborhoodList = result;
          this.cdr.markForCheck();
        });
    }
  }
  // submarket change handler
  public selectedSubMarkets(submarkets) {
    if (submarkets) {
      if (submarkets.length > 0) {
        this.getDistricts(submarkets);
        this.userPermissions.subMarket = submarkets;
      } else {
        this.districtsList = [];
      }
      this.selectedMarketsCount = submarkets.length;
    }
  }
  // district change handler
  public selectedDistricts(district) {
    if (district && district.length > 0) {
      return this.httpService
        .getNeighbourhoods(district[0].key)
        .pipe(take(1))
        .subscribe(this.onSelectedDistricts.bind(this, district));
    }
  }
  onSelectedDistricts(district, result) {
    this.neighborhoodList = result;
    this.selectedDistrictCount = district.length;
    this.userPermissions.district = district;
    this.cdr.markForCheck();
  }
  // neighbourhood change handler
  public selectedNeighbourhood(event) {
    if (event) {
      this.userPermissions.neighborhood = event;
    }
  }
  // get user permissions handler
  public setUserPermissions(event) {
    if (event) {
      this.userPermissions = event;
      this.invalidUserPermissions = Object.values(this.userPermissions).some(v => v == null);
    }
  }
  // final model submit
  public addPermission() {
    if (this.pageType === 'Admin') {
      this.businessJustification.clearValidators();
      this.businessJustification.updateValueAndValidity();
    }
    if (this.formGroup.valid) {
      this.userPermissions.status =
        this.pageType && this.pageType === 'user' ? this.permissionStatus[2] : this.permissionStatus[0];
      this.getGeographicLevels(this.userPermissions);
      this.dialogRef.close(this.recordsArray);
    } else {
      // display validation errors when clicking 'Add' so users can fix them
      Object.values(this.formGroup.controls).forEach(control => control.markAsTouched({ onlySelf: true }));
    }
  }
  public onCancel() {
    this.dialogRef.close();
  }
  public formPermissionRecord(userPermission: IUserPermission, heirarchyLevel: number) {
    const geoLevels = ['district', 'neighborhood'];
    if (userPermission[geoLevels[heirarchyLevel]] && userPermission[geoLevels[heirarchyLevel]].length > 0) {
      userPermission[geoLevels[heirarchyLevel]].forEach(object => {
        const record = new ILocationPermission();
        record[geoLevels[heirarchyLevel] + 'Id'] = object.key;
        record[geoLevels[heirarchyLevel] + 'Val'] = object.value;
        for (let index = 0; index < heirarchyLevel; index++) {
          record[geoLevels[index] + 'Id'] = userPermission[geoLevels[index]][0].key;
          record[geoLevels[index] + 'Val'] = userPermission[geoLevels[index]][0].value;
        }
        this.setUserPermissionDefaultValuesAndAddToList(record, userPermission);
      });
    }
  }
  public getGeographicLevels(userPermissions: IUserPermission) {
    let geoLevelIndex;
    if (this.userPermissions) {
      if (this.userPermissions.neighborhood && this.userPermissions.neighborhood.length > 0) {
        geoLevelIndex = 1;
        this.formPermissionRecord(userPermissions, geoLevelIndex);
      } else if (this.userPermissions.district && this.userPermissions.district.length > 0) {
        geoLevelIndex = 0;
        this.formPermissionRecord(userPermissions, geoLevelIndex);
      } else {
        const record = new ILocationPermission();
        this.setUserPermissionDefaultValuesAndAddToList(record, this.userPermissions);
      }
    }
  }
  public setUserPermissionDefaultValuesAndAddToList(record: ILocationPermission, data) {
    if (record && data) {
      record.countryId = data.country.key;
      record.countryVal = data.country.value;
      record.countryName = data.country.name;
      record.divisionId = data.division.key;
      record.divisionVal = data.division.value;
      record.divisionName = data.division.name;
      record.globalRegionId = data.globalRegion.key;
      record.globalRegionVal = data.globalRegion.value;
      record.globalRegionName = data.globalRegion.name;
      record.marketId = data.market.key;
      record.marketVal = data.market.value;
      record.marketName = data.market.name;
      record.neighborhoodId = data.neighborhood && data.neighborhood[0] ? data.neighborhood[0].key : 0;
      record.districtId = data.district && data.district[0] ? data.district[0].key : 0;
      record.subMarketId = data.submarket.key;
      record.subMarketVal = data.submarket.value;
      record.subMarketName = data.submarket.name;
      record.productTypeId = data.productType.key;
      record.productTypeVal = data.productType.value;
      record.productTypeName = data.productType.value;
      record.propertyUsageTypeId = data.productType.key;
      record.propertyUsageTypeVal = data.productType.value;
      record.propertyUsageTypeName = data.productType.value;
      record.accessRequestStatusId = data.status.key;
      record.accessRequestStatusVal = data.status.value;
      // confidentiality
      record.confidentialityTypeId = this.confidentialityFC.value.key;
      record.confidentialityTypeVal = this.confidentialityFC.value.value;
      // business justification
      record.businessJustification = this.businessJustification.value;
      record.updateBy = this.userInfo.emailAddress;
      record.createBy = this.userInfo.emailAddress;
      record.id = this.id;
      this.recordsArray.push(record);
    }
  }
  public whitespaceValidator(control: FormControl) {
    const isWhitespace = (control.value || '').trim().length === 0;
    return !isWhitespace ? null : { whitespace: true };
  }
  public get businessJustification() {
    return this.formGroup.get('businessJustification');
  }
  public get confidentialityFC() {
    return this.formGroup.get('confidentialityFC');
  }
  private setConfidentialityLevel(confidentiality: IOption) {
    this.confidentialityFC.setValue(confidentiality);
  }
  ngOnDestroy() {
    /** label translation purpose added */
  }
}
